import { normalizePosSlotLayouts } from './posSlotLayouts';

const USER_PREFERENCES_UPDATED_EVENT = 'user-preferences-updated';
const USER_PREFERENCES_STORAGE_KEY = 'userPreferencesBroadcast';
export const DEFAULT_PRINT_RECEIPT_REDIRECT = {
  enabled: false,
  delaySeconds: 10,
};
export const DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS = {
  delaySeconds: DEFAULT_PRINT_RECEIPT_REDIRECT.delaySeconds,
};
export const DEFAULT_PURCHASE_ORDER_REVIEW = {
  autoSaveItemChanges: true,
  requireItemConfirmation: true,
};

const normalizeStringList = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter((item) => item)
    : []
);

export const normalizeRedirectDelaySeconds = (value, fallback = DEFAULT_PRINT_RECEIPT_REDIRECT.delaySeconds) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed >= 1 && parsed <= 300) return parsed;
  return fallback;
};

export const normalizePrintReceiptRedirectDefaults = (
  value,
  fallback = DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS
) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const base = fallback && typeof fallback === 'object' && !Array.isArray(fallback)
    ? fallback
    : DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS;
  return {
    delaySeconds: normalizeRedirectDelaySeconds(
      source.delaySeconds,
      normalizeRedirectDelaySeconds(
        base.delaySeconds,
        DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS.delaySeconds
      )
    ),
  };
};

export const normalizePrintReceiptRedirect = (value, fallback = DEFAULT_PRINT_RECEIPT_REDIRECT) => {
  const base = fallback && typeof fallback === 'object' && !Array.isArray(fallback)
    ? fallback
    : DEFAULT_PRINT_RECEIPT_REDIRECT;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      enabled: typeof base.enabled === 'boolean' ? base.enabled : DEFAULT_PRINT_RECEIPT_REDIRECT.enabled,
      delaySeconds: normalizeRedirectDelaySeconds(base.delaySeconds, DEFAULT_PRINT_RECEIPT_REDIRECT.delaySeconds),
    };
  }
  return {
    enabled: typeof value.enabled === 'boolean'
      ? value.enabled
      : (typeof base.enabled === 'boolean' ? base.enabled : DEFAULT_PRINT_RECEIPT_REDIRECT.enabled),
    delaySeconds: normalizeRedirectDelaySeconds(
      value.delaySeconds,
      normalizeRedirectDelaySeconds(base.delaySeconds, DEFAULT_PRINT_RECEIPT_REDIRECT.delaySeconds)
    ),
  };
};

export const normalizePurchaseOrderReview = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_PURCHASE_ORDER_REVIEW };
  }
  return {
    autoSaveItemChanges: typeof value.autoSaveItemChanges === 'boolean'
      ? value.autoSaveItemChanges
      : DEFAULT_PURCHASE_ORDER_REVIEW.autoSaveItemChanges,
    requireItemConfirmation: typeof value.requireItemConfirmation === 'boolean'
      ? value.requireItemConfirmation
      : DEFAULT_PURCHASE_ORDER_REVIEW.requireItemConfirmation,
  };
};

export const normalizeUserPreferences = (value) => {
  const printReceiptRedirectDefaults = normalizePrintReceiptRedirectDefaults(
    value?.printReceiptRedirectDefaults
  );
  return {
    userId: Number.isInteger(Number(value?.userId)) ? Number(value.userId) : null,
    homeShortcuts: normalizeStringList(value?.homeShortcuts),
    posSlotLayouts: normalizePosSlotLayouts(value?.posSlotLayouts),
    posFavGesturesSeen: value?.posFavGesturesSeen === true,
    printReceiptRedirect: normalizePrintReceiptRedirect(value?.printReceiptRedirect, {
      ...DEFAULT_PRINT_RECEIPT_REDIRECT,
      delaySeconds: printReceiptRedirectDefaults.delaySeconds,
    }),
    hasCustomPrintReceiptRedirect: value?.hasCustomPrintReceiptRedirect === true,
    printReceiptRedirectDefaults,
    purchaseOrderReview: normalizePurchaseOrderReview(value?.purchaseOrderReview),
  };
};

export const normalizeUserPreferencesUpdate = (value = {}) => {
  const updates = {};
  if (Object.prototype.hasOwnProperty.call(value, 'homeShortcuts')) {
    updates.homeShortcuts = normalizeStringList(value?.homeShortcuts);
  }
  if (Object.prototype.hasOwnProperty.call(value, 'posSlotLayouts')) {
    updates.posSlotLayouts = normalizePosSlotLayouts(value?.posSlotLayouts);
  }
  if (Object.prototype.hasOwnProperty.call(value, 'posFavGesturesSeen')) {
    updates.posFavGesturesSeen = value?.posFavGesturesSeen === true;
  }
  if (Object.prototype.hasOwnProperty.call(value, 'printReceiptRedirect')) {
    updates.printReceiptRedirect = value?.printReceiptRedirect === null
      ? null
      : normalizePrintReceiptRedirect(value?.printReceiptRedirect);
  }
  if (Object.prototype.hasOwnProperty.call(value, 'purchaseOrderReview')) {
    updates.purchaseOrderReview = normalizePurchaseOrderReview(value?.purchaseOrderReview);
  }
  return updates;
};

export const broadcastUserPreferencesUpdated = (preferences) => {
  if (typeof window === 'undefined') return;
  const normalized = normalizeUserPreferences(preferences);
  window.dispatchEvent(new CustomEvent(USER_PREFERENCES_UPDATED_EVENT, {
    detail: normalized,
  }));
  try {
    window.localStorage.setItem(USER_PREFERENCES_STORAGE_KEY, JSON.stringify({
      updatedAt: Date.now(),
      preferences: normalized,
    }));
  } catch (error) {
    // Ignore storage write failures.
  }
};

export const subscribeUserPreferencesUpdates = (listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  const handleEvent = (event) => {
    listener(normalizeUserPreferences(event?.detail));
  };

  const handleStorage = (event) => {
    if (event.key !== USER_PREFERENCES_STORAGE_KEY || !event.newValue) return;
    try {
      const payload = JSON.parse(event.newValue);
      listener(normalizeUserPreferences(payload?.preferences));
    } catch (error) {
      // Ignore malformed storage payloads.
    }
  };

  window.addEventListener(USER_PREFERENCES_UPDATED_EVENT, handleEvent);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener(USER_PREFERENCES_UPDATED_EVENT, handleEvent);
    window.removeEventListener('storage', handleStorage);
  };
};
